<script lang="ts">
  import GraphView from '../components/GraphView.svelte'
  import InsightsSidebar from '../components/InsightsSidebar.svelte'

  interface StartingPoint {
    id: number
    kind: string
    name: string
  }

  interface Props {
    initialEntityId: number | null
    onNavigateHome?: () => void
  }

  let { initialEntityId, onNavigateHome }: Props = $props()

  let focalEntityId = $state<number | null>(initialEntityId)
  let depth = $state(2)
  let clusterNodeIds = $state<number[]>([])
  let startingPoints = $state<StartingPoint[]>([])
  let startingError = $state<string | null>(null)

  function handleFocalChange(entityId: number) {
    focalEntityId = entityId
    const url = new URL(window.location.href)
    url.searchParams.set('entity_id', String(entityId))
    window.history.replaceState({}, '', url.toString())
  }

  function handleClusterChange(nodeIds: number[]) {
    clusterNodeIds = nodeIds
  }

  function handlePopState() {
    const url = new URL(window.location.href)
    const param = url.searchParams.get('entity_id')
    if (param) {
      const parsed = parseInt(param, 10)
      if (!Number.isNaN(parsed)) {
        focalEntityId = parsed
      }
    }
  }

  async function loadStartingPoints() {
    try {
      const response = await fetch('/api/graph/starting_points', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) throw new Error(`starting points request failed: ${response.status}`)
      const data = await response.json()
      startingPoints = data.entities ?? []
    } catch (err) {
      startingError = err instanceof Error ? err.message : String(err)
    }
  }

  $effect(() => {
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  })

  $effect(() => {
    if (focalEntityId === null) {
      loadStartingPoints()
    }
  })
</script>

<div class="graph-page" data-testid="graph-page">
  <header class="graph-header">
    <button class="graph-back" type="button" onclick={() => onNavigateHome?.()}>← terug</button>
    <h1 class="graph-title">Graph view</h1>
    {#if focalEntityId !== null}
      <label class="graph-depth-label">
        Diepte:
        <select bind:value={depth} class="graph-depth-select" data-testid="graph-depth">
          <option value={1}>1</option>
          <option value={2}>2</option>
          <option value={3}>3</option>
        </select>
      </label>
    {/if}
  </header>
  {#if focalEntityId === null}
    <div class="graph-empty">
      <p>Geen focal entiteit gekozen. Kies een startpunt op basis van de drukste knooppunten in het corpus:</p>
      {#if startingError}
        <p class="graph-empty-error">Kon startpunten niet laden: {startingError}</p>
      {:else if startingPoints.length === 0}
        <p>Nog geen entiteiten met links beschikbaar.</p>
      {:else}
        <ul class="graph-starting-list" data-testid="starting-points">
          {#each startingPoints as point (point.id)}
            <li>
              <button
                type="button"
                class="graph-starting-pill"
                onclick={() => handleFocalChange(point.id)}
              >
                <span class="graph-starting-kind">{point.kind}</span>
                {point.name}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {:else}
    <div class="graph-layout">
      <div class="graph-canvas-pane">
        <GraphView
          entityId={focalEntityId}
          {depth}
          onFocalChange={handleFocalChange}
          onClusterChange={handleClusterChange}
        />
      </div>
      <aside class="graph-sidebar-pane">
        <InsightsSidebar focalEntityId={focalEntityId} clusterNodeIds={clusterNodeIds} />
      </aside>
    </div>
  {/if}
</div>

<style>
  .graph-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: #f1f5f9;
  }
  .graph-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1.25rem;
    background: white;
    border-bottom: 1px solid #e2e8f0;
  }
  .graph-back {
    background: none;
    border: 1px solid #cbd5e1;
    border-radius: 0.375rem;
    padding: 0.25rem 0.75rem;
    color: #475569;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .graph-back:hover {
    color: #0f172a;
    border-color: #94a3b8;
  }
  .graph-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #0f172a;
  }
  .graph-depth-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: #475569;
  }
  .graph-depth-select {
    border: 1px solid #cbd5e1;
    border-radius: 0.25rem;
    padding: 0.15rem 0.4rem;
    font-size: 0.85rem;
    background: white;
  }
  .graph-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #64748b;
    text-align: center;
    gap: 0.75rem;
    padding: 2rem;
  }
  .graph-empty-error {
    color: #b91c1c;
  }
  .graph-starting-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding: 0;
    max-width: 720px;
  }
  .graph-starting-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.85rem;
    border: 1px solid #cbd5e1;
    background: white;
    border-radius: 9999px;
    cursor: pointer;
    font-size: 0.85rem;
    color: #0f172a;
  }
  .graph-starting-pill:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }
  .graph-starting-kind {
    text-transform: uppercase;
    font-size: 0.7rem;
    color: #94a3b8;
  }
  .graph-layout {
    flex: 1;
    display: grid;
    grid-template-columns: 3fr 2fr;
    min-height: 0;
  }
  .graph-canvas-pane {
    padding: 1rem;
    overflow: hidden;
  }
  .graph-sidebar-pane {
    overflow: hidden;
  }
</style>
